import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { Subscription } from 'rxjs';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NzSelectSizeType } from 'ng-zorro-antd/select';
import { GainsFormItemComponent } from '../gains-form-item/gains-form-item.component';
import { ResizedEvent } from '../../directives/resized.event';

@Component({
  selector: 'gains-select',
  standalone: false,
  templateUrl: './gains-select.component.html',
  styleUrl: './gains-select.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GainsSelectComponent),
      multi: true
    }
  ]
})
export class GainsSelectComponent extends GainsFormItemComponent implements AfterViewInit, ControlValueAccessor, OnChanges, OnInit, OnDestroy {
  private static instanceCounter = 0;
  private static idCounter = 0;

  @Input() items: SelectOption[] = [];

  /**
   * You can create new options in tags mode
   * (by pressing Enter if the value in the input field does not exist).
   * You can't extend the options like that using multiple mode.
   */
  @Input() mode: 'multiple' | 'tags' | 'default' = 'default';

  /*
   * Max option size inside the dropdown, overflow when exceed the size
   */
  @Input() optionOverflowSize = 8;

  /**
   * Forces the max number of tags on the top control to dynamically change accordingly to width used. Default enabled.
   */
  @Input() forceMaxTagsToFitWidth = true;

  /**
   * Max tag count beforing adding ellipsis. Default 9999.
   */
  @Input() maxTagCount = 9999;
  @Input() allowClear = true;
  @Input() allCheckbox = false;
  @Input() loading = false;
  /** Typing in the select filters its items by title. */
  @Input() showSearch = false;
  /**
   * The value is each item's `value` (`'InboundSku'`, `['Queued', 'Failed']`) rather than the item itself: for a select
   * standing for a plain value, such as a filter.
   */
  @Input() byValue = false;
  /** Names the select for screen readers when it has no label. */
  @Input() ariaLabel?: string;
  /** The id of its input, for a `<label for>`: unique per select unless given. */
  @Input() selectId = `gains-select-${GainsSelectComponent.idCounter++}`;
  @Input() size: NzSelectSizeType = 'default';
  /** False lets the dropdown grow wider than the select, for long items. */
  @Input() dropdownMatchSelectWidth = true;
  @Output() valueChanges = new EventEmitter<any>();

  isSelectOpen = false;
  /** The items under their group, in the order the groups first come; a group without a label for the items without one. */
  optionGroups: { label: string | null; items: SelectOption[] }[] = [];
  /** With `byValue`, the value last given or chosen: picked again among new items. */
  private _value: any = null;
  private _onTouched: () => void = () => undefined;
  private readonly _element: HTMLElement = inject(ElementRef).nativeElement;
  private _formChanges?: Subscription;
  private _ownChanges?: Subscription;
  /** Disabled by its form, apart from `disabledState`: either one disables the select. */
  private _formDisabled = false;
  className = 'not-forcing-max-tags';
  selectTopControlElement?: Element;

  constructor(private readonly _changeDetectorRef: ChangeDetectorRef) {
    super();
  }
  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  /** Disabled by its form (`control.disable()`, `[disabled]` with ngModel). */
  setDisabledState(isDisabled: boolean): void {
    this._formDisabled = isDisabled;
    this.applyDisabled();
  }

  /** Leaving the select touches the form's control: its errors show from then on. */
  onBlur(): void {
    this._onTouched();
  }
  registerOnChange(fn: any): void {
    this._formChanges?.unsubscribe();
    this._formChanges = this.internalFormControl?.valueChanges.subscribe(value => {
      this._value = this.toModel(value);
      fn(this._value);
    });
  }
  /** Items coming after the value (loaded later, or changed) select it again: by value, it is only known by its value. */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['items']) {
      this.optionGroups = groupItems(this.items);
      if (!changes['items'].firstChange && this.byValue) {
        this.writeValue(this._value);
      }
    }
    if (changes['disabledState'] && !changes['disabledState'].firstChange) {
      this.applyDisabled();
    }
    if (changes['ariaLabel'] && !changes['ariaLabel'].firstChange) {
      this.labelInput();
    }
  }

  ngOnInit(): void {
    this.setControl();
    this.optionGroups = groupItems(this.items);
    this._ownChanges = this.internalFormControl?.valueChanges.subscribe(value => {
      this.onModelChange(value);
    });
  }

  ngOnDestroy(): void {
    this._formChanges?.unsubscribe();
    this._ownChanges?.unsubscribe();
  }

  onModelChange(values: any) {
    this.recalculateTagsUsageOfSpace(values);
    this.checkError();
  }
  onResized(event: ResizedEvent) {
    this.recalculateTagsUsageOfSpace(this.internalFormControl?.value || []);
  }
  /**
   * if forceMaxTagsToFitWidth is true, then see if tags fit inside top control element, add ellipsis if not.
   */
  recalculateTagsUsageOfSpace(values: any[]): void {
    if (this.forceMaxTagsToFitWidth) {
      this.maxTagCount = 50;
      const controlElementw = this.selectTopControlElement?.clientWidth;
      if (!controlElementw || !values) {
        return;
      }
      let sumOf = 0;
      for (let i = 0; i < values.length; i++) {
        if (values[i] == '__all__') {
          continue;
        }
        // Estimate of element width. 10 pixels per letter and 5 pixels for border.
        const iw = values[i].title.length * 10 + 5;
        sumOf += iw;

        // See if all elements still fit so far plus ellipsis and plus clear icon?
        if (sumOf + 65 > controlElementw) {
          // No, the sum already extrapolated the max width, change the max tags.
          this.maxTagCount = i;
          break;
        }
      }
    }
  }

  ngAfterViewInit(): void {
    this.setTopControlElement();
    this.labelInput();
  }

  /** The label for screen readers goes on the input the select focuses, not on its wrapper. */
  private labelInput(): void {
    const input = this._element.querySelector('nz-select input');
    if (!input) return;
    if (this.ariaLabel) {
      input.setAttribute('aria-label', this.ariaLabel);
    } else {
      input.removeAttribute('aria-label');
    }
  }

  /**
   * if forceMaxTagsToFitWidth is true, then obtain the nz-select-top-control of this select component.
   */

  setTopControlElement(): void {
    if (this.forceMaxTagsToFitWidth) {
      this.className = `force-max-tags-${GainsSelectComponent.instanceCounter++}`;
      this._changeDetectorRef?.detectChanges();

      const selectElement = document.getElementsByClassName(this.className);
      if (selectElement[0]) {
        this.selectTopControlElement = selectElement[0].getElementsByTagName('nz-select-top-control')[0];
      }
    }
  }

  onSelectOpenChange(isOpen: boolean): void {
    this.isSelectOpen = isOpen;
    this.checkError();
    // Remove only the browser default tooltip/title from option containers, not from custom tooltip elements
    if (isOpen) {
      setTimeout(() => {
        const overlayOptions = document.querySelectorAll('.ant-select-item-option');
        overlayOptions.forEach(option => {
          // Remove title only if the element is not an icon or does not have nz-tooltip
          if (option.getAttribute('title')) {
            option.removeAttribute('title');
          }
        });
      }, 0);
    }
  }

  override writeValue(value: any): void {
    let selectedValue;
    if (this.byValue) {
      this._value = value;
      selectedValue = Array.isArray(value) ? this.items.filter(item => value.includes(item.value)) : (this.items.find(item => item.value === value) ?? null);
    } else if (Array.isArray(value)) {
      selectedValue = value.map(v => this.items.find(item => item.title === v.title) ?? v);
    } else {
      const found = this.items.find(item => item.title === value?.title);
      selectedValue = found;
    }

    this.internalFormControl?.setValue(selectedValue, { emitEvent: false });
    this._changeDetectorRef?.detectChanges();
  }

  isAllSelected(): boolean {
    return this.internalFormControl.value?.length === this.items.length;
  }
  toggleAll(event: any): void {
    event.stopPropagation();
    if (this.isAllSelected()) {
      this.internalFormControl.setValue([]);
    } else {
      this.internalFormControl.setValue(this.items);
    }
  }
  isIndeterminate(): boolean {
    const count = this.internalFormControl.value?.length ?? 0;
    return count > 0 && count < this.items.length;
  }

  get selectedArray(): any[] {
    const v = this.internalFormControl?.value;
    if (Array.isArray(v)) return v;
    if (v == null) return [];
    return [v];
  }

  onValueChange(selectedValue: any): void {
    this.valueChanges.emit(this.toModel(selectedValue));
  }

  private applyDisabled(): void {
    const disabled = this.disabledState || this._formDisabled;
    if (!this.internalFormControl || this.internalFormControl.disabled === disabled) {
      return;
    }
    if (disabled) {
      this.internalFormControl.disable({ emitEvent: false });
    } else {
      this.internalFormControl.enable({ emitEvent: false });
    }
  }

  /** The selected items as the parent takes them: their values with `byValue`, the items themselves otherwise. */
  private toModel(selected: any): any {
    if (!this.byValue) {
      return selected;
    }
    return Array.isArray(selected) ? selected.map(item => item?.value) : (selected?.value ?? null);
  }
}

/** The items under their group, the groups in the order they first come. */
function groupItems(items: SelectOption[] | null | undefined): { label: string | null; items: SelectOption[] }[] {
  const groups = new Map<string | null, SelectOption[]>();
  for (const item of items ?? []) {
    const label = item.group ?? null;
    groups.set(label, [...(groups.get(label) ?? []), item]);
  }
  return [...groups].map(([label, grouped]) => ({ label, items: grouped }));
}

export class SelectOption {
  title!: string;
  /** What the select stands for with `byValue`: an id, a name, a number. */
  value?: any;
  /** Shows the item under this group's heading. */
  group?: string;
  /** A short note on the right of the item, such as a count. */
  hint?: string;
  icon?: string;
  disabledOption?: boolean;
  showOnRight?: boolean;
  tooltip?: string;
}
